package pl.nickers.match3.ui

import androidx.compose.animation.core.Animatable
import androidx.compose.foundation.background
import androidx.compose.foundation.layout.Arrangement
import androidx.compose.foundation.layout.Row
import androidx.compose.foundation.layout.padding
import androidx.compose.foundation.layout.size
import androidx.compose.foundation.shape.RoundedCornerShape
import androidx.compose.material.icons.Icons
import androidx.compose.material.icons.filled.Diamond
import androidx.compose.material3.Icon
import androidx.compose.material3.Text
import androidx.compose.runtime.Composable
import androidx.compose.runtime.LaunchedEffect
import androidx.compose.runtime.getValue
import androidx.compose.runtime.mutableStateOf
import androidx.compose.runtime.remember
import androidx.compose.runtime.setValue
import androidx.compose.ui.Alignment
import androidx.compose.ui.Modifier
import androidx.compose.ui.draw.scale
import androidx.compose.ui.graphics.Color
import androidx.compose.ui.text.font.FontWeight
import androidx.compose.ui.unit.dp
import androidx.compose.ui.unit.sp
import kotlinx.coroutines.delay
import pl.nickers.match3.GemEarnEvent
import pl.nickers.match3.GemEarnReason

/**
 * E18/F18.4 — the gem balance chip shown on the Start, Journey, and in-game screens.
 *
 * Purely presentational: it renders the [balance] it is handed and never computes
 * or mutates an amount (all gem arithmetic lives in `WalletService`). The chip
 * pulses briefly whenever the balance changes so a credit is noticeable.
 */
@Composable
internal fun GemChip(
    balance: Int,
    modifier: Modifier = Modifier,
) {
    val pulse = remember { Animatable(1f) }
    var previousBalance by remember { mutableStateOf(balance) }
    LaunchedEffect(balance) {
        if (balance != previousBalance) {
            previousBalance = balance
            pulse.animateTo(1.25f)
            pulse.animateTo(1f)
        }
    }

    Row(
        modifier = modifier
            .scale(pulse.value)
            .background(GrimoirePalette.ChipInk, shape = RoundedCornerShape(12.dp))
            .padding(horizontal = 10.dp, vertical = 6.dp),
        verticalAlignment = Alignment.CenterVertically,
        horizontalArrangement = Arrangement.spacedBy(4.dp),
    ) {
        Icon(
            imageVector = Icons.Filled.Diamond,
            contentDescription = "Gems",
            tint = GrimoirePalette.Gold,
            modifier = Modifier.size(18.dp),
        )
        Text(
            text = "$balance",
            color = GrimoirePalette.Cream,
            fontSize = 18.sp,
            fontWeight = FontWeight.Bold,
            maxLines = 1,
        )
    }
}

/** How long an earn toast stays on screen before it is dismissed. */
private const val EARN_TOAST_DURATION_MS = 1800L

/**
 * Transient "+N 💎" feedback shown when gems are credited (E18/F18.4).
 *
 * [event] comes straight from the wallet; [onConsumed] tells the wallet the
 * feedback has been shown so the same credit is not replayed on recomposition.
 */
@Composable
internal fun GemEarnToast(
    event: GemEarnEvent?,
    onConsumed: () -> Unit,
    modifier: Modifier = Modifier,
) {
    if (event == null) return
    LaunchedEffect(event.sequence) {
        delay(EARN_TOAST_DURATION_MS)
        onConsumed()
    }
    Row(
        modifier = modifier
            .background(GrimoirePalette.ChipInk, shape = RoundedCornerShape(12.dp))
            .padding(horizontal = 12.dp, vertical = 8.dp),
        verticalAlignment = Alignment.CenterVertically,
        horizontalArrangement = Arrangement.spacedBy(6.dp),
    ) {
        Icon(
            imageVector = Icons.Filled.Diamond,
            contentDescription = null,
            tint = GrimoirePalette.Gold,
            modifier = Modifier.size(18.dp),
        )
        Text(
            text = "+${event.amount}",
            color = Color.White,
            fontSize = 18.sp,
            fontWeight = FontWeight.Bold,
        )
        Text(
            text = event.reason.label(),
            color = GrimoirePalette.Cream,
            fontSize = 13.sp,
        )
    }
}

/**
 * Player-facing wording for an earn reason.
 *
 * ASSUMPTION: strings are hard-coded English here, matching the rest of the UI —
 * the project has no localization layer yet.
 */
private fun GemEarnReason.label(): String = when (this) {
    GemEarnReason.STARTER_GRANT -> "welcome gift"
    GemEarnReason.FIRST_LEVEL_WIN -> "first clear"
    GemEarnReason.STAR_MILESTONE -> "star milestone"
    GemEarnReason.PURCHASE -> "purchase"
}
